import React, { useState, useEffect, forwardRef } from 'react';
import cx from 'classnames';

import SelectPicker from '@components/SelectPicker';
import { SelectPickerValue, SelectPickerOptionProps } from '@components/SelectPicker/types';
import BottomSheet from '@primitives/BottomSheet';
import Button from '@primitives/Button';
import Chip from '@primitives/Chip';
import AddIcon from '@icons/AddIcon';
import CloseIcon from '@icons/CloseIcon';
import Typography from '@primitives/Typography';
import RequiredIcon from '@icons/RequiredIcon';

import { useDeepCompareEffect } from '@hooks';

import { MobileMultiSelectProps, SelectedOptionType } from './types';
import { transformSelectedOptions, sortedOptions } from './utils';
import { AllOption, MobileMultiSelectConstants } from './constants';
import styles from './MobileMultiSelect.module.scss';

const MobileMultiSelect = forwardRef<HTMLDivElement, MobileMultiSelectProps>(
    (
        {
            options,
            selectedOptions = [],
            placeholder = 'Select',
            searchPlaceholder = '',
            bottomSheetTitle = 'Select',
            limitChipThreshold = 0,
            updateSelectedOptions,
            enableSelectedOptionSorting = false,
            showSelectAll = false,
            selectAllSubtext = '',
            limitChipThresholdMsg = '',
            disabled = false,
            labelClasses = '',
            required,
        },
        ref,
    ) => {
        const [pickerOptions, setPickerOptions] = useState<Array<SelectPickerOptionProps>>(options);
        const [selectedValues, setSelectedValues] = useState<Array<SelectPickerValue>>([]);
        const [optionsToDisplay, setOptionsToDisplay] = useState<Array<SelectedOptionType>>([]);
        const [selectedOptionsFromPicker, setSelectedOptionsFromPicker] = useState<
            Array<SelectedOptionType>
        >([]);
        const [isOpen, setIsOpen] = useState<boolean>(false);
        const [clearAllState, setClearAllState] = useState<boolean>(false);

        const { moreCTA, clearAllCTA, applyCTA } = MobileMultiSelectConstants;

        useEffect(() => {
            if (options?.length) {
                setPickerOptions(options);
            }
        }, [options]);

        useDeepCompareEffect(() => {
            if (!selectedOptions?.length) {
                setSelectedValues(selectedOptions);
                const selectedOptionsToDisplay: Array<SelectPickerOptionProps> = [];
                setOptionsToDisplay(selectedOptionsToDisplay);

                if (updateSelectedOptions) {
                    updateSelectedOptions([]);
                }

                return;
            }

            setSelectedValues(selectedOptions);

            /** Set the options as chip */
            const selectedOptionsMap = new Map(selectedOptions.map((option) => [option, option]));
            const selectedOptionsToDisplay: Array<SelectPickerOptionProps> = [];
            options?.forEach((option) => {
                if (selectedOptionsMap.has(option?.value)) {
                    selectedOptionsToDisplay.push(option);
                }
            });
            setOptionsToDisplay(transformSelectedOptions(selectedOptionsToDisplay));
            if (enableSelectedOptionSorting) {
                const transformedOptions = transformSelectedOptions(selectedOptionsToDisplay);
                const sortedPickerOptions = sortedOptions(options, transformedOptions);

                if (Array.isArray(sortedPickerOptions)) {
                    setPickerOptions(sortedPickerOptions);
                    setSelectedValues(transformedOptions?.map((option) => option?.value));
                }
            }
        }, [selectedOptions]);

        const handleBottomSheetState = (bool: boolean): void => {
            setIsOpen(bool);
            setClearAllState(false);
        };

        const onSelectionChange = (option: SelectPickerOptionProps[] | SelectPickerOptionProps) => {
            const transformedOptions = transformSelectedOptions(option);
            setSelectedOptionsFromPicker(transformedOptions);
        };

        const removeOptionChip = (
            e: React.MouseEvent<SVGElement> | React.TouchEvent<SVGElement>,
            option: SelectedOptionType,
        ): void => {
            e?.stopPropagation();
            const filteredOptions: SelectedOptionType[] = optionsToDisplay?.filter(
                (item) => item?.id !== option?.id,
            );
            setOptionsToDisplay(filteredOptions);
            setSelectedValues(filteredOptions.map((option) => option.value));
            if (updateSelectedOptions) {
                updateSelectedOptions(filteredOptions);
            }
        };

        const resetOptionsToDisplay = (
            e: React.MouseEvent<SVGElement> | React.TouchEvent<SVGElement>,
        ): void => {
            e?.stopPropagation();
            setOptionsToDisplay([]);
            setSelectedValues([]);
            setClearAllState(true);
            if (updateSelectedOptions) {
                updateSelectedOptions([]);
            }
        };

        const applySelectedOptions = (): void => {
            setOptionsToDisplay(selectedOptionsFromPicker);
            setSelectedValues(selectedOptionsFromPicker?.map((option) => option?.value));
            setIsOpen(false);
            const sortedPickerOptions = sortedOptions(options, selectedOptionsFromPicker);
            if (Array.isArray(sortedPickerOptions) && enableSelectedOptionSorting) {
                setPickerOptions(sortedPickerOptions);
                setSelectedValues(selectedOptionsFromPicker?.map((option) => option?.value));
            }

            if (updateSelectedOptions) {
                updateSelectedOptions(selectedOptionsFromPicker);
            }
        };

        const clearSelectedOptions = (): void => {
            setClearAllState(true);
            setSelectedValues([]);
        };

        const returnSelectedOptionsAsChips = (): JSX.Element | null => {
            if (optionsToDisplay?.length) {
                const remainingChipCount = optionsToDisplay?.length - limitChipThreshold;
                const finalOptions =
                    limitChipThreshold > 0
                        ? optionsToDisplay?.slice(0, limitChipThreshold)
                        : optionsToDisplay;

                if (showSelectAll && optionsToDisplay.length === pickerOptions.length) {
                    return (
                        <div className={styles['mobile-multi-select__chip-container']}>
                            <Chip
                                key={AllOption.value}
                                label={AllOption.label}
                                wrapperClasses={styles['mobile-multi-select__chip']}
                                onChipClick={(e) => e?.stopPropagation()}
                                RightAdornment={
                                    <CloseIcon onClick={(e) => resetOptionsToDisplay(e)} />
                                }
                                disabled={disabled}
                            />
                        </div>
                    );
                }

                return (
                    <div className={styles['mobile-multi-select__chip-container']}>
                        {finalOptions?.map((option) => (
                            <Chip
                                key={option?.id}
                                label={option?.label}
                                wrapperClasses={styles['mobile-multi-select__chip']}
                                labelClasses={styles['mobile-multi-select__chip-label']}
                                onChipClick={(e) => e?.stopPropagation()}
                                RightAdornment={
                                    <CloseIcon onClick={(e) => removeOptionChip(e, option)} />
                                }
                                disabled={disabled}
                            />
                        ))}
                        {limitChipThreshold && remainingChipCount > 0 ? (
                            <span
                                data-testid="mobile-multi-select-limit-content"
                                className={styles['mobile-multi-select__limit-count-content']}
                            >
                                <AddIcon />
                                <Typography as="span" variant="p2">
                                    {remainingChipCount}{' '}
                                    {limitChipThresholdMsg?.length
                                        ? limitChipThresholdMsg
                                        : moreCTA}
                                </Typography>
                            </span>
                        ) : null}
                    </div>
                );
            }
            return null;
        };

        const returnClearAllIcon = (): JSX.Element | null => {
            if (optionsToDisplay?.length) {
                return (
                    <CloseIcon data-testid="close-icon" onClick={(e) => resetOptionsToDisplay(e)} />
                );
            }
            if (!optionsToDisplay?.length && !isOpen) {
                return (
                    <Typography variant="p2" className={styles['mobile-multi-select__placeholder']}>
                        {placeholder}
                    </Typography>
                );
            }
            return null;
        };

        const handleSelectAll = (
            option: SelectPickerOptionProps[] | SelectPickerOptionProps,
        ): void => {
            onSelectionChange(option);
        };

        return (
            <div ref={ref} className={cx(styles['mobile-multi-select'])}>
                {(isOpen || optionsToDisplay?.length > 0) && (
                    <div className={styles['mobile-multi-select__label']}>
                        <Typography
                            variant="p5"
                            className={cx(
                                styles['mobile-multi-select__label--content'],
                                labelClasses,
                            )}
                        >
                            {placeholder}
                        </Typography>
                        {required && (
                            <RequiredIcon
                                className={styles['mobile-multi-select__label--required']}
                            />
                        )}
                    </div>
                )}
                <div
                    data-testid="mobile-multi-select"
                    className={cx(styles['mobile-multi-select__chip-wrapper'], {
                        [styles['mobile-multi-select--disabled']]: disabled,
                    })}
                    onClick={() => handleBottomSheetState(true)}
                >
                    {returnSelectedOptionsAsChips()}
                    {returnClearAllIcon()}
                </div>
                <BottomSheet
                    data-testid="mobile-multi-select-bottomSheet"
                    open={isOpen}
                    onClose={() => handleBottomSheetState(false)}
                    usePadding={false}
                >
                    <div className={styles['mobile-multi-select__bottom-sheet-title-wrapper']}>
                        <Typography variant="h2">{bottomSheetTitle}</Typography>
                        <CloseIcon onClick={() => handleBottomSheetState(false)} />
                    </div>
                    <SelectPicker
                        selectedValues={selectedValues}
                        onSelectionChange={onSelectionChange}
                        options={pickerOptions}
                        placeholder={searchPlaceholder}
                        clearAll={clearAllState}
                        searchBarClass={styles['mobile-multi-select__select-picker-search']}
                        wrapperClasses={styles['mobile-multi-select__select-picker-wrapper']}
                        selectPickerItemClassName={
                            styles['mobile-multi-select__select-picker-option']
                        }
                        selectAllClass={styles['mobile-multi-select__select-all']}
                        showSelectAll={showSelectAll}
                        updateSelectedOptions={handleSelectAll}
                        selectAllSubtext={selectAllSubtext}
                        multiSelect
                        hideBottomCTAs
                        showSearchBar
                    />
                    <div className={styles['mobile-multi-select__button-container']}>
                        <Button
                            variant="secondary"
                            onClick={clearSelectedOptions}
                            data-testid="mobile-multi-select-clear-all"
                            className={styles['mobile-multi-select__button-container_button']}
                        >
                            {clearAllCTA}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={applySelectedOptions}
                            data-testid="mobile-multi-select-apply"
                            className={styles['mobile-multi-select__button-container_button']}
                        >
                            {applyCTA}
                        </Button>
                    </div>
                </BottomSheet>
            </div>
        );
    },
);

export default MobileMultiSelect;
